
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UserStats {
  userId: string;
  postCount: number;
  photoCount: number;
  videoCount: number;
  totalFileSize: number;
}

export const useUserStats = (userIds: string[]) => {
  return useQuery({
    queryKey: ['user-stats', userIds],
    queryFn: async (): Promise<UserStats[]> => {
      if (userIds.length === 0) return [];

      const stats = await Promise.all(
        userIds.map(async (userId) => {
          // Get post count
          const { count: postCount } = await supabase
            .from('memory_posts')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId);

          // Get photo count (including all image types)
          const { count: photoCount } = await supabase
            .from('media_files')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .in('media_type', ['image_original', 'image_edited', 'image_avatar', 'ai_generated_image']);

          // Get video count (including all video types)
          const { count: videoCount } = await supabase
            .from('media_files')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .in('media_type', ['video_original', 'video_edited', 'ai_generated_video']);

          // Get total file size in bytes
          const { data: fileSizeData } = await supabase
            .from('media_files')
            .select('size_bytes')
            .eq('user_id', userId);

          const totalFileSize = fileSizeData?.reduce((total, file) => total + (file.size_bytes || 0), 0) || 0;

          return {
            userId,
            postCount: postCount || 0,
            photoCount: photoCount || 0,
            videoCount: videoCount || 0,
            totalFileSize,
          };
        })
      );

      return stats;
    },
    enabled: userIds.length > 0,
  });
};
